// OCR-Zuschnitt-Modal — bewegliches Bild + Zoom-Slider (react-easy-crop).
//
// 04.02.2026 (Rewrite v3): Der User hat explizit dieses UI-Muster
// gewünscht — Bild verschieben statt Rechteck ziehen, Zoom per Slider,
// und Buttons die IMMER funktionieren. Vorher gabs zwei Probleme:
//   • Touch-Displays konnten das Rechteck nicht ziehen (nur onMouse*).
//   • Die Aktion-Buttons „Ganzes Bild" / „Zuschneiden" lösten manchmal
//     keine sichtbare Reaktion aus, weil der Ausschnitt-Overlay Clicks
//     abgefangen hat.
// Fix: react-easy-crop übernimmt Pointer/Touch/Wheel-Handling, die
// Buttons liegen sauber im Modal-Footer außerhalb der Crop-Fläche.
import { useState, useCallback, useEffect } from 'react';
import Cropper from 'react-easy-crop';
import { Button } from './ui/button';
import { X, Check, RotateCcw, Loader2 } from 'lucide-react';

async function loadImage(src) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = reject;
    img.src = src;
  });
}

async function getCroppedBlob(imageSrc, cropPixels) {
  const img = await loadImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = Math.max(1, Math.round(cropPixels.width));
  canvas.height = Math.max(1, Math.round(cropPixels.height));
  const ctx = canvas.getContext('2d');
  ctx.drawImage(
    img,
    cropPixels.x, cropPixels.y, cropPixels.width, cropPixels.height,
    0, 0, canvas.width, canvas.height,
  );
  return new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.92));
}

export default function ImageCropDialog({ file, hintLabel, onCropped, onCancel }) {
  const [imgSrc, setImgSrc] = useState(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedPixels, setCroppedPixels] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (e) => setImgSrc(e.target.result);
    reader.onerror = () => setError('Bild konnte nicht geladen werden.');
    reader.readAsDataURL(file);
  }, [file]);

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCroppedPixels(areaPixels);
  }, []);

  const confirm = async () => {
    if (busy) return;
    if (!imgSrc || !croppedPixels) {
      // Fallback: das ganze Bild verwenden
      onCropped(file);
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const blob = await getCroppedBlob(imgSrc, croppedPixels);
      if (!blob) throw new Error('Zuschnitt fehlgeschlagen');
      const cropped = new File(
        [blob],
        (file.name || 'crop').replace(/\.[^.]+$/, '') + '_crop.jpg',
        { type: 'image/jpeg', lastModified: Date.now() },
      );
      onCropped(cropped);
    } catch (e) {
      setError(String(e.message || e));
      setBusy(false);
    }
  };

  const useFullImage = () => {
    if (busy) return;
    onCropped(file);
  };

  return (
    <div
      className="fixed inset-0 z-[60] bg-black/60 flex items-center justify-center p-2 sm:p-4"
      data-testid="image-crop-dialog"
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onPointerDown={(e) => e.stopPropagation()}
      onTouchStart={(e) => e.stopPropagation()}
    >
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-3xl max-h-[95vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-4 sm:px-5 py-3 border-b border-gray-100 shrink-0">
          <div className="min-w-0 flex-1">
            <h3 className="text-sm font-semibold text-[#32373c] truncate">Bereich für OCR zuschneiden</h3>
            <p className="text-[11px] text-gray-500 truncate">
              {hintLabel || 'Bild verschieben und Zoom nutzen — der Rahmen bleibt fest'}
            </p>
          </div>
          <button
            onClick={onCancel}
            className="ml-2 text-gray-400 hover:text-gray-600 p-1"
            data-testid="btn-crop-cancel"
            aria-label="Schließen"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body — Cropper-Fläche */}
        <div className="relative bg-black/90 flex-1 min-h-[280px] sm:min-h-[400px]">
          {imgSrc ? (
            <Cropper
              image={imgSrc}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={undefined}          // freies Seitenverhältnis
              minZoom={1}
              maxZoom={5}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onCropComplete}
              restrictPosition
              showGrid
              zoomWithScroll
              // dunkles Overlay + heller Rahmen, damit der Ausschnitt klar erkennbar ist
              style={{
                containerStyle: { backgroundColor: 'rgba(0,0,0,0.9)' },
                cropAreaStyle: {
                  border: '2px solid #9ec043',
                  color: 'rgba(0, 0, 0, 0.6)',
                },
              }}
            />
          ) : (
            <div className="absolute inset-0 flex items-center justify-center text-white/70 text-sm">
              Lade Bild …
            </div>
          )}
        </div>

        {/* Zoom-Slider */}
        <div className="px-4 sm:px-5 py-3 border-t border-gray-100 bg-gray-50 shrink-0">
          <div className="flex items-center gap-3">
            <span className="text-[10px] uppercase tracking-wider text-gray-500 font-semibold shrink-0 w-12">Zoom</span>
            <input
              type="range"
              min={1}
              max={5}
              step={0.05}
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="flex-1 accent-[#9ec043]"
              data-testid="crop-zoom-slider"
              aria-label="Zoom"
            />
            <span className="text-xs text-gray-500 tabular-nums w-14 text-right">{zoom.toFixed(2)}×</span>
          </div>
          <p className="text-[11px] text-gray-400 mt-1.5">
            Ziehe das Bild an die gewünschte Position und passe den Zoom an. Der grüne Rahmen zeigt den Ausschnitt.
          </p>
          {error && (
            <div className="mt-2 text-[11px] text-red-600 bg-red-50 border border-red-200 rounded px-2 py-1">
              {error}
            </div>
          )}
        </div>

        {/* Footer — Buttons */}
        <div className="px-3 sm:px-5 py-3 border-t border-gray-100 flex flex-wrap gap-2 justify-between shrink-0 bg-white">
          <Button
            variant="outline"
            onClick={useFullImage}
            disabled={busy}
            className="text-xs sm:text-sm h-9"
            data-testid="btn-crop-full"
          >
            <RotateCcw className="w-3.5 h-3.5 mr-1" /> Ganzes Bild
          </Button>
          <div className="flex gap-2 flex-wrap ml-auto">
            <Button
              variant="outline"
              onClick={onCancel}
              disabled={busy}
              className="text-xs sm:text-sm h-9"
              data-testid="btn-crop-cancel-footer"
            >
              Abbrechen
            </Button>
            <Button
              onClick={confirm}
              disabled={busy || !imgSrc}
              className="bg-[#9ec043] hover:bg-[#8aad38] text-white text-xs sm:text-sm h-9"
              data-testid="btn-crop-confirm"
            >
              {busy
                ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Verarbeite …</>
                : <><Check className="w-3.5 h-3.5 mr-1" /> Zuschneiden &amp; OCR starten</>}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
